import 'reflect-metadata';
import type {
  AppExposedApis,
  ComponentRef,
  EditorPlatformApp,
  EditorSDK,
} from '@wix/platform-editor-sdk';
import type {
  EditorReadyFn,
  GetAppManifestFn,
  OnEventFn,
} from '@wix/yoshi-flow-editor';
import { createFedopsLogger } from './services/FedopsLogger/FedopsLogger';
import type { PlatformActions } from './types/PlatformTypes';
import { Services } from './ioc/services';
import { container } from './ioc/container';
import { initDynamicServices } from './ioc/initDynamicServices';

export const editorReady: EditorReadyFn = async (
  editorSDK,
  appDefinitionId,
  options,
  flowApi,
) => {
  const fedopsLogger = createFedopsLogger(options);
  fedopsLogger.appLoadStarted();

  initDynamicServices(
    editorSDK,
    appDefinitionId,
    options,
    flowApi,
    fedopsLogger,
  );

  const monitoring = await container.getAsync(Services.Monitoring);
  await monitoring.transaction('editor-bootstrap').execute(async () => {
    const editorApp = await container.getAsync(Services.EditorApp);

    await editorApp.editorReady(appDefinitionId, options);
  });
  fedopsLogger.appLoaded();
};

export const onEvent: OnEventFn = (event) => {
  const editorApp = container.get(Services.EditorApp);
  return editorApp.onEvent(event);
};

export const handleAction: EditorPlatformApp['handleAction'] = (action) => {
  const editorApp = container.get(Services.EditorApp);
  return editorApp.handleAction(action as PlatformActions);
};

export const getAppManifest: GetAppManifestFn = () => {
  const editorApp = container.get(Services.EditorApp);
  return editorApp.getAppManifest();
};

export const publicExports = (editorSDK: EditorSDK): AppExposedApis => ({
  public: {
    getBindingDefinitions: (compRef: ComponentRef) => {
      const editorApp = container.get(Services.EditorApp);

      return editorApp.getBindingDefinitions(compRef);
    },
  },
  editor: {
    getSelectedWidgetDesignPresetId: async ({ widgetRef }) => {
      return (
        await editorSDK.application.appStudioWidgets.getPreset('token', {
          componentRef: widgetRef,
        })
      )?.style;
    },
  },
});
// otherwise jest throws: Duplicate identifier 'exports'. Compiler reserves name 'exports' in top level scope of a module
export { publicExports as exports };
